'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import LeadCard from './LeadCard';
import LeadStats from './LeadStats';
import LeadFilters from './LeadFilters';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  whatsapp: string;
  message: string;
  bookingDetails: {
    type: 'flight' | 'hotel' | 'holiday';
    from?: string;
    to?: string;
    destination?: string;
    date?: string;
    passengers?: number;
    price?: number;
  };
  timestamp: string;
  status: 'new' | 'contacted' | 'quoted' | 'booked' | 'cancelled';
  bookingType: 'lead' | 'direct'; // New field to distinguish lead vs direct booking
  paymentStatus?: 'pending' | 'paid' | 'refunded'; // For direct bookings
}

const LeadDashboardInteractive = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filter, setFilter] = useState<'all' | 'new' | 'contacted' | 'quoted' | 'booked' | 'cancelled'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Load leads from localStorage
    const storedLeads = JSON.parse(localStorage.getItem('bookingLeads') || '[]');
    // Sort by timestamp descending (newest first)
    storedLeads.sort((a: Lead, b: Lead) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    setLeads(storedLeads);
  }, []);

  const updateLeadStatus = (index: number, newStatus: Lead['status']) => {
    const updatedLeads = [...leads];
    updatedLeads[index].status = newStatus;
    setLeads(updatedLeads);
    localStorage.setItem('bookingLeads', JSON.stringify(updatedLeads));
  };

  const deleteLead = (index: number) => {
    const updatedLeads = leads.filter((_, i) => i !== index);
    setLeads(updatedLeads);
    localStorage.setItem('bookingLeads', JSON.stringify(updatedLeads));
  };

  const filteredLeads = leads.filter(lead => {
    const matchesFilter = filter === 'all' || lead.status === filter;
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    quoted: leads.filter(l => l.status === 'quoted').length,
    booked: leads.filter(l => l.status === 'booked').length,
    cancelled: leads.filter(l => l.status === 'cancelled').length,
    directBookings: leads.filter(l => l.bookingType === 'direct').length,
    leadInquiries: leads.filter(l => l.bookingType === 'lead').length,
    totalRevenue: leads
      .filter(l => l.status === 'booked')
      .reduce((sum, l) => sum + (l.bookingDetails.price || 0), 0),
    pendingPayments: leads
      .filter(l => l.bookingType === 'direct' && l.paymentStatus === 'pending')
      .reduce((sum, l) => sum + (l.bookingDetails.price || 0), 0),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/5">
      {/* Header */}
      <div className="bg-white border-b border-border/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                <Icon name="ChartBarIcon" size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-black text-foreground">
                  Lead Dashboard
                </h1>
                <p className="text-sm text-muted-foreground font-bold">
                  Manage your booking inquiries
                </p>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/booking-interest'}
              className="bg-gradient-to-r from-primary to-secondary text-white font-bold px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <Icon name="PlusIcon" size={18} />
              <span>New Lead</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <LeadStats stats={stats} />

        {/* Filters & Search */}
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-accent/20 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or phone..."
                  className="w-full pl-10 pr-4 py-3 rounded-lg border-2 border-border focus:border-primary focus:outline-none transition-colors"
                />
              </div>
            </div>
            <LeadFilters currentFilter={filter} onFilterChange={setFilter} />
          </div>
        </div>

        {/* Leads List */}
        {filteredLeads.length === 0 ? (
          <div className="bg-white rounded-xl p-12 shadow-lg border-2 border-accent/20 text-center">
            <Icon name="InboxIcon" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-heading font-black text-foreground mb-2">
              No Leads Found
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || filter !== 'all' ?'Try adjusting your filters or search term' :'Start collecting leads from your booking interest form'}
            </p>
            <button
              onClick={() => window.location.href = '/booking-interest'}
              className="bg-gradient-to-r from-primary to-secondary text-white font-bold px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 inline-flex items-center gap-2"
            >
              <Icon name="PlusIcon" size={18} />
              <span>Create Test Lead</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredLeads.map((lead, index) => (
              <LeadCard
                key={index}
                lead={lead}
                onStatusChange={(newStatus) => updateLeadStatus(index, newStatus)}
                onDelete={() => deleteLead(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadDashboardInteractive;